import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Briefcase, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const Careers = () => {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCareers();
  }, []);

const CodeSkyLoader = ({ text = "Loading opportunities..." }) => {
  return (
    <div className="flex items-center justify-center min-h-[60vh] bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        
        {/* Spinner */}
        <div className="w-14 h-14 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />

        {/* Brand Text */}
        <div className="text-center">
          <h2 className="text-xl font-heading font-bold text-gradient">
            CodeSky
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {text}
          </p>
        </div>
      </div>
    </div>
  );
};



  const fetchCareers = async () => {
    try {
      const response = await api.get('/careers?active_only=true');
      setCareers(response.data);
    } catch (error) {
      toast.error('Failed to load careers');
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();



  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-blue-50/30" data-testid="careers-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <h1 className="text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6">
              Join Our <span className="text-gradient">Team</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Build your career with CodeSky. We're looking for talented individuals passionate about technology and innovation.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          {loading ? (
       <CodeSkyLoader text="Loading opportunities..." />
) : careers.length === 0 ? (

            <div className="text-center py-20">
              <p className="text-slate-600 text-lg">No open positions at the moment. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {careers.map((career, index) => (
                <motion.div
  key={career.id}
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ delay: index * 0.05 }}
  className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-xl transition-all duration-300 flex flex-col"
>
  {/* TOP CONTENT */}
  <div className="flex-1">
    {/* Job Title – ONE LINE */}
    <h3 className="text-xl font-heading font-bold text-slate-900 mb-2 truncate">
      {career.title}
    </h3>

    {/* Meta info – ONE LINE */}
    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-3">
      <span className="flex items-center gap-1">
        <Briefcase className="w-4 h-4" />
        {career.department}
      </span>
      <span className="flex items-center gap-1">
        <MapPin className="w-4 h-4" />
        {career.location}
      </span>
      <span className="flex items-center gap-1">
        <Clock className="w-4 h-4" />
        {career.job_type}
      </span>
    </div>

    {/* Description – FIXED HEIGHT */}
    <p className="text-slate-600 text-sm leading-relaxed">
       {career.description.length > 120
    ? career.description.slice(0, 120) + '...'
    : career.description}
    </p>

    {career.description.length > 120 && (
     <button
  onClick={() => navigate(`/careers/${career.id}`)}
  className="mt-2 text-sm font-medium text-primary hover:underline"
>
  Read More
</button>
    )}
  </div>

  {/* APPLY BUTTON – ALWAYS AT BOTTOM */}
   <Button
  onClick={() => navigate(`/careers/${career.id}`)}
  variant="outline"
  className="
    mt-6 w-full
    border-primary text-primary
    hover:bg-primary hover:text-white
    hover:border-primary
    transition-colors
  "
>
  View Details
</Button>

</motion.div>

              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;
